<template>
  <v-card
    class="training-agent pa-8"
    outlined
  >
    <div class="header-zone mb-7">
      <h3 class="title-glow tracking-tight">🎮 AI Command Training</h3>
      <h4 class="subtitle-glow">
        Completed commands will be marked <span class="highlighted">complete</span>!
      </h4>
    </div>

    <v-row class="cmd-grid" dense>
      <v-col
        v-for="cmd in commands"
        :key="cmd.type"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <div
          :class="['cmd-card', { completed: cmd.completed }]"
        >
          <div class="icon-area">
            <transition name="pop">
              <v-icon
                v-if="cmd.completed"
                color="success"
                large
              >mdi-check-circle</v-icon>
              <v-icon
                v-else
                color="grey lighten-1"
                large
              >mdi-checkbox-blank-circle-outline</v-icon>
            </transition>
          </div>
          <div class="cmd-info">
            <h4>{{ cmd.display }}</h4>
            <div class="cmd-desc">{{ cmd.example }}</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <div class="score-area mt-7">
      <v-progress-linear
        :value="completionPercent"
        color="cyan accent-2"
        height="18"
        rounded
        striped
        background-opacity="0.12"
        class="mb-3 progress-bar"
      />
      <v-divider class="my-8" />
      <div class="score-num">
        <span class="score-big">{{ completedCount }}</span>
        <span class="score-total">/ {{ commands.length }} Commands Complete</span>
      </div>
      <v-btn class="mt-5" color="cyan darken-3" dark large rounded elevation="5" @click="resetTraining">
        <v-icon left>mdi-reload</v-icon>
        Reset Progress
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import { EventBus } from "@/eventBus"; // Adjust as needed

export default {
  name: "TrainingAgent",
  data() {
    return {
      commands: [
        { type: "google", display: "Google Search", example: 'Say "google Taylor Swift"', completed: false },
        { type: "youtube", display: "YouTube Search", example: 'Say "youtube Billie Eilish"', completed: false },
        { type: "research", display: "AI Research", example: 'Say "research Drake"', completed: false },
        { type: "computer", display: "Computer Control", example: 'Say "computer play music"', completed: false },
      ],
      completedCount: 0,
    };
  },
  computed: {
    completionPercent() {
      return (this.completedCount / this.commands.length) * 100;
    },
  },
  created() {
    EventBus.$on("commandExecuted", this.markCommandComplete);
  },
  beforeDestroy() {
    EventBus.$off("commandExecuted", this.markCommandComplete);
  },
  methods: {
    markCommandComplete({ transcript }) {
      const idx = this.commands.findIndex(cmd =>
        transcript.toLowerCase().includes(cmd.type)
      );
      if (idx !== -1 && !this.commands[idx].completed) {
        this.$set(this.commands[idx], "completed", true);
        this.completedCount = this.commands.filter(cmd => cmd.completed).length;
        this.$emit("celebrate"); // Optionally trigger confetti/sound
      }
    },
    resetTraining() {
      this.commands.forEach(cmd => (cmd.completed = false));
      this.completedCount = 0;
    },
  },
};
</script>

<style scoped>
.training-agent {
  background: linear-gradient(120deg, #121d33 80%, #182c46 100%);
  border-radius: 22px;
  max-width: 1500px;
  margin: 56px auto 44px auto;
  box-shadow: 0 2px 40px #00eaff45, 0 1.5px 0px #00eaffaa inset;
  border: 2.5px solid #1bd6f6b8;
  position: relative;
  overflow: hidden;
}

.header-zone {
  text-align: center;
}
.title-glow {
  color: #10eaff;
  font-family: 'Orbitron', 'Rajdhani', 'Share Tech Mono', sans-serif;
  font-size: 2.14rem;
  text-shadow: 0 0 18px #2af6ffcc, 0 0 2px #00eaffee;
  letter-spacing: 0.03em;
  margin-bottom: 0.15em;
}
.subtitle-glow {
  color: #96eaff;
  font-family: 'Share Tech Mono', 'Rajdhani', 'Orbitron', sans-serif;
  font-size: 1.18rem;
  font-weight: 500;
  text-shadow: 0 0 6px #13bcfd55;
  margin-top: 0.35em;
}
.highlighted {
  color: #1be6b1;
  text-shadow: 0 0 8px #5cffea;
}

.cmd-grid {
  margin-top: 24px;
  margin-bottom: 10px;
}
.cmd-card {
  display: flex;
  align-items: flex-start;
  gap: 17px;
  min-height: 140px;
  border-radius: 17px;
  padding: 20px 20px 20px 20px;
  margin: 20px 20px 20px 20px;
  color: #fff;
  background: rgba(255,255,255,0.10);
  box-shadow: 0 2px 20px #00eaff2e, 0 0 0 transparent;
  transition: box-shadow 0.35s, background 0.25s, border 0.25s, transform 0.2s;
  border: 2.5px solid transparent;
  cursor: pointer;
  position: relative;
}
.cmd-card:hover {
  box-shadow: 0 2px 40px #00eaff66, 0 2px 24px #20ffe1cc;
  background: linear-gradient(100deg, #16f6e3cc 3%, #121d33 89%);
  transform: scale(1.027);
}
.cmd-card.completed {
  border: 2.5px solid #20ffe1;
  background: linear-gradient(120deg, #1debb7b5 0%, #0f3e7e99 110%);
  box-shadow: 0 2px 40px #29ffc7dd;
  color: #0d3231;
  animation: shine 1s 1;
}
@keyframes shine {
  0% { box-shadow: 0 2px 24px #17ffd5cc, 0 0 0 #00eaff00; }
  35% { box-shadow: 0 2px 56px #29ffdcdd, 0 0 0 #0ff; }
  70% { box-shadow: 0 2px 32px #20ffe1cc, 0 0 12px #0ff8; }
  100% { box-shadow: 0 2px 40px #00eaff66; }
}
.icon-area {
  margin-top: 2px;
  margin-right: 3px;
  min-width: 36px;
}
.cmd-info h4 {
  font-size: 1.22rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 7px;
  font-family: 'Orbitron', 'Share Tech Mono', Arial, sans-serif;
  text-shadow: 0 1px 7px #0feaff88;
  letter-spacing: 0.01em;
}
.cmd-desc {
  font-size: 0.98rem;
  color: #e3e8ed;
  font-family: 'Share Tech Mono', monospace;
  opacity: 0.92;
  letter-spacing: 0.01em;
  padding-left: 3px;
}

.score-area {
  text-align: center;
  margin-top: 22px;
  margin-block: 16px;
  padding-right: 20px;
  padding-left: 20px;
}
.progress-bar {
  box-shadow: 0 2px 18px #00eaff66;
  border-radius: 10px;
  width: 100% ;
}
.score-num {
  font-size: 2.2rem;
  font-family: 'Orbitron', 'Share Tech Mono', sans-serif;
  color: #20ffe1;
  margin-bottom: 2px;
  letter-spacing: 0.01em;
  text-shadow: 0 0 8px #20ffe199;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 12px;
}
.score-big {
  font-size: 2.35rem;
  font-weight: 800;
  margin-right: 7px;
  color: #20ffe1;
}
.score-total {
  font-size: 1.18rem;
  color: #8ee0fa;
}
.v-btn {
  font-family: 'Orbitron', 'Rajdhani', 'Share Tech Mono', Arial, sans-serif;
  font-size: 1.08rem;
  text-transform: none;
  letter-spacing: 0.02em;
  font-weight: 600;
}
.pop-enter-active,
.pop-leave-active {
  transition: transform 0.24s cubic-bezier(0.4, 0, 0.2, 1);
}
.pop-enter, .pop-leave-to {
  transform: scale(0.5);
  opacity: 0;
}
</style>
